import * as Sentry from "@sentry/react";
import { AlertTriangle, Link2, RefreshCw } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { MetaLine } from "@/components/ui/meta-line";
import { Spinner } from "@/components/ui/spinner";
import { Tag } from "@/components/ui/tag";

import { FilesystemAPI } from "../../caseFiles/api/filesystemApi";
import { nodeTitle } from "../../caseFiles/nodeNames";
import type { FsNode } from "../../caseFiles/types";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { draftTitle, formName } from "../workspace/workspaceModel";

type Props = {
  node: FsNode;
  /** The copy's bytes were replaced; the pane reloads them. */
  onUpdated: (node: FsNode) => void;
};

const when = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

/**
 * Above a file copied from Work Products: a From Work Products tag and the
 * snapshot's date with a way back to the source, or, once the source has
 * changed, Draft Changed with Update Copy and Keep This Version.
 */
export const CopySourceStrip: FC<Props> = ({ node, onUpdated }) => {
  const { individualId, open, invalidate } = useCaseIde();
  const { fs, workspace } = useCaseData();
  const [busy, setBusy] = useState<"update" | "keep" | null>(null);
  const ref = node.source_ref;
  if (!ref) return null;

  const noun = ref.kind === "form" ? "form" : "draft";
  const Noun = ref.kind === "form" ? "Form" : "Draft";
  const draft =
    ref.kind === "draft"
      ? workspace.drafts.find((d) => d.draft_id === ref.id)
      : undefined;
  const form =
    ref.kind === "form"
      ? workspace.forms.find(
          (f) => f.case_extend_uscis_filled_form_id === ref.id
        )
      : undefined;
  const sourceTitle = draft ? draftTitle(draft) : form ? formName(form) : null;
  const sourceGone = workspace.complete && !draft && !form;

  const run = async (keep: boolean) => {
    setBusy(keep ? "keep" : "update");
    try {
      const result = await FilesystemAPI.updateCopy(individualId, node.id, {
        keep
      });
      await fs.refresh();
      if (!keep) {
        invalidate(["fs", "packet"]);
        onUpdated(result.node);
        const label = result.node.exhibit_label;
        toast.success(`Updated ${nodeTitle(result.node)} from the ${noun}`, {
          description: label
            ? `Exhibit ${label} keeps its place; the packet will rebuild.`
            : `The copy now matches the ${noun}.`
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Couldn't update the copy."
      );
    } finally {
      setBusy(null);
    }
  };

  if (node.source_changed) {
    return (
      <div
        className="flex h-9 shrink-0 items-center gap-2 border-b border-line-1 bg-warn-soft pr-2 pl-3"
        data-testid="copy-source-changed"
      >
        <AlertTriangle className="size-3.5 shrink-0 text-warn" />
        <MetaLine
          className="min-w-0 flex-1 text-fg"
          parts={[`${Noun} changed since added`, when(ref.changed_at)]}
        />
        <Button
          variant="ghost"
          size="xs"
          disabled={busy !== null}
          onClick={() => void run(true)}
        >
          {busy === "keep" ? <Spinner className="size-3" /> : null}
          Keep This Version
        </Button>
        <Button
          size="xs"
          disabled={busy !== null || sourceGone}
          title={sourceGone ? `The ${noun} was deleted` : undefined}
          onClick={() => void run(false)}
        >
          {busy === "update" ? <Spinner className="size-3" /> : <RefreshCw />}
          Update Copy
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex h-8 shrink-0 items-center gap-2 border-b border-line-1 pr-2 pl-3"
      data-testid="copy-source"
    >
      <Tag>
        <Link2 />
        From Work Products
      </Tag>
      <MetaLine
        className="min-w-0 flex-1"
        parts={[
          sourceTitle
            ? `Snapshot of ${sourceTitle}`
            : `Snapshot of the ${noun}`,
          when(ref.promoted_at)
        ]}
      />
      {draft || form ? (
        <Button
          variant="ghost"
          size="xs"
          onClick={() =>
            open({ kind: ref.kind, id: ref.id, title: sourceTitle ?? Noun })
          }
        >
          Open {Noun}
        </Button>
      ) : null}
    </div>
  );
};
